// src/components/SmartFlow/ExecutionViewer.jsx
import React, { useState, useEffect } from "react";
import ReactFlow, { Background, Controls } from "reactflow";
import "reactflow/dist/style.css";
import ExecutionLogsViewer from "./ExecutionLogsViewer";

const ExecutionViewer = ({ execution, workflow, onClose }) => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    if (!workflow || !execution) return;

    // Build nodes with execution status highlighting
    const executedNodes = execution.result_data?.executed_nodes || [];
    const currentNode = execution.result_data?.current_node;
    const errorNode = execution.result_data?.error_node;

    const highlightedNodes = workflow.nodes.map((node) => {
      let backgroundColor = "white";
      let borderColor = "#ddd";
      let borderWidth = 2;

      // Check execution status
      const wasExecuted = executedNodes.includes(node.id);
      const isCurrent = currentNode === node.id;
      const hasError = errorNode === node.id;

      if (hasError) {
        backgroundColor = "#fee";
        borderColor = "#e74c3c";
        borderWidth = 3;
      } else if (isCurrent) {
        backgroundColor = "#fff9e6";
        borderColor = "#f39c12";
        borderWidth = 3;
      } else if (wasExecuted) {
        backgroundColor = "#eeffee";
        borderColor = "#2ecc71";
        borderWidth = 2;
      }

      return {
        id: node.id,
        type: node.node_type,
        position: { x: node.position_x || 0, y: node.position_y || 0 },
        data: {
          label: node.node_type,
          config: node.config,
        },
        style: {
          backgroundColor,
          border: `${borderWidth}px solid ${borderColor}`,
          borderRadius: "8px",
          padding: "12px",
          minWidth: "150px",
        },
      };
    });

    // Build edges with execution path highlighting
    const executedEdges = execution.result_data?.executed_edges || [];
    const highlightedEdges = workflow.edges.map((edge) => {
      const wasExecuted = executedEdges.includes(edge.id);

      return {
        id: edge.id,
        source: edge.source_node_id,
        target: edge.target_node_id,
        sourceHandle: edge.condition || undefined,
        animated: wasExecuted,
        style: {
          stroke: wasExecuted ? "#2ecc71" : "#b1b1b7",
          strokeWidth: wasExecuted ? 3 : 2,
        },
      };
    });

    setNodes(highlightedNodes);
    setEdges(highlightedEdges);
  }, [workflow, execution]);

  const getStatusBadge = () => {
    if (!execution) return null;

    const statusColors = {
      SUCCESS: { bg: "#d4edda", text: "#155724" },
      FAILED: { bg: "#f8d7da", text: "#721c24" },
      RUNNING: { bg: "#fff3cd", text: "#856404" },
      PENDING: { bg: "#d1ecf1", text: "#0c5460" },
    };

    const color = statusColors[execution.status] || statusColors.PENDING;

    return (
      <span
        style={{
          padding: "4px 12px",
          backgroundColor: color.bg,
          color: color.text,
          borderRadius: "4px",
          fontSize: "12px",
          fontWeight: "bold",
        }}
      >
        {execution.status}
      </span>
    );
  };

  if (!workflow || !execution) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#7f8c8d" }}>
        No execution data available
      </div>
    );
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "8px",
          width: "90%",
          height: "90%",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px",
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: "18px" }}>
              Execution Visualization: {workflow.name}
            </h2>
            <div style={{ marginTop: "8px", display: "flex", gap: "15px", fontSize: "13px" }}>
              <span>Status: {getStatusBadge()}</span>
              <span style={{ color: "#7f8c8d" }}>
                Started: {new Date(execution.started_at).toLocaleString()}
              </span>
              {execution.completed_at && (
                <span style={{ color: "#7f8c8d" }}>
                  Completed: {new Date(execution.completed_at).toLocaleString()}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <button
              onClick={() => setShowLogs(true)}
              style={{
                padding: "8px 16px",
                background: "#3498db",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              📋 View Logs
            </button>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "24px",
                cursor: "pointer",
                color: "#7f8c8d",
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Canvas */}
        <div style={{ flex: 1, position: "relative" }}>
          <ReactFlow nodes={nodes} edges={edges} fitView>
            <Background />
            <Controls />
          </ReactFlow>
        </div>

        {/* Legend */}
        <div
          style={{
            padding: "15px 20px",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            gap: "20px",
            fontSize: "12px",
            backgroundColor: "#f9f9f9",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #2ecc71",
                backgroundColor: "#eeffee",
                borderRadius: "3px",
              }}
            />
            <span>Completed</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "3px solid #f39c12",
                backgroundColor: "#fff9e6",
                borderRadius: "3px",
              }}
            />
            <span>In Progress</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "3px solid #e74c3c",
                backgroundColor: "#fee",
                borderRadius: "3px",
              }}
            />
            <span>Error</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #ddd",
                backgroundColor: "white",
                borderRadius: "3px",
              }}
            />
            <span>Not Executed</span>
          </div>
        </div>

        {/* Error Message */}
        {execution.status === "FAILED" && execution.error_message && (
          <div
            style={{
              padding: "15px 20px",
              backgroundColor: "#f8d7da",
              color: "#721c24",
              borderTop: "1px solid #f5c6cb",
            }}
          >
            <strong>Error:</strong> {execution.error_message}
          </div>
        )}
      </div>

      {/* Execution Logs Viewer Modal */}
      {showLogs && (
        <ExecutionLogsViewer
          executionId={execution.id}
          onClose={() => setShowLogs(false)}
        />
      )}
    </div>
  );
};

export default ExecutionViewer;
